import { Text } from "~/components/Text"
import { VideoPlayer } from "~/components/VideoPlayer"
import { useEffect, useMemo, useState } from "react"
import { ScissorOutlined } from "@ant-design/icons"
import { Button, Flex, Modal, Slider } from "antd"
import { Alert } from "~/components/Alert"
import { formatTime, roundToDecimals } from "~/common/utils"
import styles from "./index.css"

const DEFAULT_MIN_CLIP_DURATION = 4
const DEFAULT_MAX_CLIP_DURATION = 15
const TIMELINE_FRAME_COUNT = 8
const TIMELINE_TICK_COUNT = 10
const TIME_PRECISION = 10

type VideoClipModalProps = {
  duration: number
  end_time: number
  maxDuration?: number
  minDuration?: number
  open: boolean
  start_time: number
  videoUrl: string
  onCancel: () => void
  onConfirm: (start_time: number, end_time: number) => void
}

const floorToOneDecimal = (value: number) => {
  return Math.floor(value * TIME_PRECISION) / TIME_PRECISION
}

const getDurationMessage = (
  duration: number,
  minDuration: number,
  maxDuration: number,
) => {
  if (duration < minDuration) return `不能少于 ${minDuration} 秒`
  if (duration > maxDuration) return `不能超过 ${maxDuration} 秒`

  return "时长符合要求"
}

export const VideoClipModal = ({
  duration,
  end_time,
  maxDuration = DEFAULT_MAX_CLIP_DURATION,
  minDuration = DEFAULT_MIN_CLIP_DURATION,
  open,
  start_time,
  videoUrl,
  onCancel,
  onConfirm,
}: VideoClipModalProps) => {
  const [draftRange, setDraftRange] = useState<[number, number]>([
    start_time,
    end_time,
  ])
  const maxTimelineValue = Math.max(0.1, floorToOneDecimal(duration))
  const selectedDuration = roundToDecimals(draftRange[1] - draftRange[0])
  const isDurationValid =
    selectedDuration >= minDuration && selectedDuration <= maxDuration
  const timelineFrames = useMemo(
    () =>
      Array.from({ length: TIMELINE_FRAME_COUNT }, (_, index) =>
        Math.min(
          maxTimelineValue,
          (maxTimelineValue * index) / (TIMELINE_FRAME_COUNT - 1),
        ),
      ),
    [maxTimelineValue],
  )
  const timelineTicks = useMemo(
    () =>
      Array.from({ length: TIMELINE_TICK_COUNT }, (_, index) =>
        Math.min(
          maxTimelineValue,
          (maxTimelineValue * index) / (TIMELINE_TICK_COUNT - 1),
        ),
      ),
    [maxTimelineValue],
  )

  useEffect(() => {
    if (!open) return

    const nextRange: [number, number] = [
      roundToDecimals(Math.min(start_time, maxTimelineValue)),
      roundToDecimals(Math.min(end_time || maxDuration, maxTimelineValue)),
    ]

    setDraftRange(nextRange)
  }, [end_time, maxDuration, maxTimelineValue, open, start_time])

  const updateRange = (range: number[]) => {
    const nextRange: [number, number] = [
      roundToDecimals(range[0]),
      roundToDecimals(range[1]),
    ]

    setDraftRange(nextRange)
  }

  return (
    <Modal
      centered
      destroyOnHidden
      footer={
        <Button
          disabled={!isDurationValid}
          icon={<ScissorOutlined />}
          type="primary"
          onClick={() =>
            onConfirm(
              roundToDecimals(draftRange[0]),
              roundToDecimals(draftRange[1]),
            )
          }
        >
          确认截取此片段
        </Button>
      }
      open={open}
      width={585}
      onCancel={onCancel}
      title={
        <Flex
          align="center"
          gap={12}
        >
          <Flex
            align="center"
            className={styles.clipTitleIcon}
            justify="center"
          >
            <ScissorOutlined />
          </Flex>
          <Flex vertical>
            <Text>截取视频片段</Text>
            <Text
              size="s"
              type="secondary"
            >
              仅支持复刻 {minDuration}-{maxDuration}{" "}
              秒片段，拖拽两端手柄调整区间
            </Text>
          </Flex>
        </Flex>
      }
    >
      <Flex
        className={styles.clipModalBody}
        gap={22}
        vertical
      >
        <VideoPlayer
          controls
          muted
          className={styles.clipPreviewPlayer}
          endTime={draftRange[1]}
          objectFit="contain"
          src={videoUrl}
          startTime={draftRange[0]}
        />

        <Flex
          gap={6}
          vertical
        >
          <div className={styles.clipTimelineStage}>
            <div className={styles.clipTimelineFrames}>
              {timelineFrames.map((timestamp, index) => (
                <VideoPlayer
                  aria-hidden
                  className={styles.clipTimelineFrame}
                  key={`${timestamp}-${index}`}
                  muted
                  preload="metadata"
                  src={`${videoUrl.split("#")[0]}#t=${timestamp}`}
                />
              ))}
            </div>
            <Slider
              className={styles.clipTimelineOverlaySlider}
              max={maxTimelineValue}
              min={0}
              range={{ draggableTrack: true }}
              step={0.1}
              tooltip={{ formatter: (value) => formatTime(value || 0) }}
              value={draftRange}
              onChange={updateRange}
            />
          </div>
          <Flex justify="space-between">
            {timelineTicks.map((timestamp, index) => (
              <Text
                size={10}
                className={styles.clipTick}
                key={`${timestamp}-${index}`}
                type="secondary"
              >
                {formatTime(timestamp)}
              </Text>
            ))}
          </Flex>
        </Flex>

        <Alert
          level={isDurationValid ? "weak" : "strong"}
          showIcon={false}
          title={
            <Flex
              justify="space-between"
              align="center"
            >
              <Flex gap={16}>
                <Flex vertical>
                  <Text type="secondary">开始</Text>
                  <Text strong>{formatTime(draftRange[0])}</Text>
                </Flex>
                <Text className={styles.clipArrow}>→</Text>
                <Flex vertical>
                  <Text type="secondary">结束</Text>
                  <Text strong>{formatTime(draftRange[1])}</Text>
                </Flex>
                <Flex vertical>
                  <Text type="secondary">已选时长</Text>
                  <Text
                    className={
                      isDurationValid ? styles.validText : styles.errorText
                    }
                    strong
                  >
                    {selectedDuration.toFixed(1)}秒
                  </Text>
                </Flex>
              </Flex>
              <Text
                className={
                  isDurationValid ? styles.validText : styles.errorText
                }
              >
                {getDurationMessage(selectedDuration, minDuration, maxDuration)}
              </Text>
            </Flex>
          }
        />

        <Text
          className={styles.clipHint}
          type="secondary"
        >
          拖动橙色手柄调整起止点 · 也可拖拽中间区域整体平移
        </Text>
      </Flex>
    </Modal>
  )
}
